import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  GitCompare,
  TrendingUp,
  TrendingDown,
  Minus,

} from 'lucide-react';
import type { BudgetRevision } from '@/types/budget.types';

interface RevisionCompareSelectorProps {
  revisions: BudgetRevision[];
  /** Controls delta unit and dropdown version prefix. Defaults to 'resource'. */
  budgetLayer?: 'resource' | 'cost' | 'sales';
  onCompare: (revisionA: BudgetRevision, revisionB: BudgetRevision) => void;
  initialRevAId?: string;
  initialRevBId?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function revisionLabel(r: BudgetRevision): string {
  const date = new Date(r.createdAt ?? r.date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return `${r.revisionNumber} — ${date}`;
}

function DeltaChip({ value, unit }: { value: number; unit: 'h' | '£' }) {
  if (value === 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
        No change
      </span>
    );
  }
  const positive = value > 0;
  const label = unit === '£'
    ? `${positive ? '+' : ''}£${Math.abs(value).toLocaleString()}`
    : `${positive ? '+' : ''}${value.toLocaleString()}h`;

  return (
    <span
      className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
        positive ? 'bg-accent-warning-50 text-accent-warning-900' : 'bg-accent-success-50 text-accent-success-900'
      }`}
    >
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {label}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RevisionCompareSelector({
  revisions,
  budgetLayer = 'resource',
  onCompare,
  initialRevAId,
  initialRevBId,
}: RevisionCompareSelectorProps) {
  const isCostSales = budgetLayer === 'cost' || budgetLayer === 'sales';
  // Default: use passed-in IDs, or fall back to current vs previous
  const defaultAId = initialRevAId || String(revisions[0]?.revisionId ?? revisions[0]?.id ?? '');
  const defaultBId =
    initialRevBId ||
    (revisions.length > 1
      ? String(revisions[1]?.revisionId ?? revisions[1]?.id ?? '')
      : '');

  const [revAId, setRevAId] = useState<string>(defaultAId);
  const [revBId, setRevBId] = useState<string>(defaultBId);

  const findRevision = (id: string): BudgetRevision | undefined =>
    revisions.find((r) => String(r.revisionId ?? r.id) === id);

  const revA = findRevision(revAId);
  const revB = findRevision(revBId);

  // Delta preview between the two selected revisions
  const deltaHours = useMemo(() => {
    if (!revA || !revB) return 0;
    return (revA.hoursChange ?? 0) - (revB.hoursChange ?? 0);
  }, [revA, revB]);

  const deltaBudget = useMemo(() => {
    if (!revA || !revB) return 0;
    return (revA.budgetChange ?? 0) - (revB.budgetChange ?? 0);
  }, [revA, revB]);



  const canCompare = revA && revB && revAId !== revBId;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <GitCompare className="h-4 w-4" />
          Compare Revisions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Selector row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Revision A */}
          <div className="space-y-1 flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-muted-foreground">From Revision</label>
            <Select value={revAId} onValueChange={setRevAId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select a revision" />
              </SelectTrigger>
              <SelectContent>
                {revisions.map((r) => (
                  <SelectItem key={String(r.revisionId ?? r.id)} value={String(r.revisionId ?? r.id)}>
                    <span className="flex items-center gap-1.5">
                      {revisionLabel(r)}
                      {r.status === 'current' && (
                        <Badge variant="outline" className="text-xs ml-1 h-4 bg-accent-success-50 text-accent-success-900 border-accent-success-500">Current</Badge>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="text-sm text-muted-foreground pt-5">vs</div>

          {/* Revision B */}
          <div className="space-y-1 flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-muted-foreground">To Revision</label>
            <Select value={revBId} onValueChange={setRevBId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select a revision" />
              </SelectTrigger>
              <SelectContent>
                {revisions.map((r) => (
                  <SelectItem key={String(r.revisionId ?? r.id)} value={String(r.revisionId ?? r.id)}>
                    <span className="flex items-center gap-1.5">
                      {revisionLabel(r)}
                      {r.status === 'current' && (
                        <Badge variant="outline" className="text-xs ml-1 h-4 bg-accent-success-50 text-accent-success-900 border-accent-success-500">Current</Badge>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Delta preview */}
        {canCompare && (
          <div className="flex flex-wrap items-center gap-3 p-3 bg-muted rounded-md">
            <span className="text-xs text-muted-foreground font-medium">
              {isCostSales ? 'Budget delta:' : 'Hours delta:'}
            </span>
            <DeltaChip value={isCostSales ? deltaBudget : deltaHours} unit={isCostSales ? '£' : 'h'} />
            {revAId === revBId && (
              <span className="text-xs text-muted-foreground italic">
                Select two different revisions to compare
              </span>
            )}
          </div>
        )}

        {revAId === revBId && revAId !== '' && (
          <p className="text-xs text-warning-600">
            Please select two different revisions to compare.
          </p>
        )}

        {/* Compare button */}
        <Button
          size="sm"
          disabled={!canCompare}
          onClick={() => {
            if (revA && revB) onCompare(revA, revB);
          }}
          className="w-full sm:w-auto"
        >
          <GitCompare className="h-4 w-4 mr-1.5" />
          Compare Revisions
        </Button>
      </CardContent>
    </Card>
  );
}
